// @contexts/ListingInferenceReviewPortalClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from "react";

import type { OptionTs } from "@ps-bridge/interop";
import { requireContext } from "@contexts/requireContext";
import {
    DefaultListingInferenceReviewPortalClient,
    type ListingInferenceReviewPortalClient,
} from "@services/clients/ListingInferenceReviewPortalClient";
import { StubListingInferenceReviewPortalClient } from "@services/stubs/StubListingInferenceReviewPortalClient";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import * as Option from "@utils/Option";

const ListingInferenceReviewPortalClientContext = createContext<OptionTs<ListingInferenceReviewPortalClient>>(
    Option.none(),
);

type ListingInferenceReviewPortalClientProviderProps = {
    readonly children: ReactNode;
    readonly client: OptionTs<ListingInferenceReviewPortalClient>;
};

export const ListingInferenceReviewPortalClientProvider: React.FC<
    ListingInferenceReviewPortalClientProviderProps
> = ({ children, client: injectedClient }) => {
    const client = useMemo<ListingInferenceReviewPortalClient>(() => {
        return Option.fold(
            injectedClient,
            () =>
                enabledFeatures[FeaturesEnum.StubClients]
                    ? new StubListingInferenceReviewPortalClient(Option.none())
                    : new DefaultListingInferenceReviewPortalClient(),
            (value) => value,
        );
    }, [injectedClient]);

    const contextValue = useMemo(() => Option.some(client), [client]);

    return (
        <ListingInferenceReviewPortalClientContext.Provider value={contextValue}>
            {children}
        </ListingInferenceReviewPortalClientContext.Provider>
    );
};

export const useListingInferenceReviewPortalClient = (): ListingInferenceReviewPortalClient => {
    return requireContext(
        useContext(ListingInferenceReviewPortalClientContext),
        "useListingInferenceReviewPortalClient must be used within a ListingInferenceReviewPortalClientProvider",
    );
};
